import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAuth } from "./auth";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const accusedStatusOptions = [
  "Not Arrested",
  "Arrested",
  "BBA",
  "On Bail",
  "Interim-173",
  "Complete Challan",
  "PO Challaned-512",
  "Aquital",
];

const formatDisplayDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const CaseFirDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canEdit, canDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("Add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [enquiryOptions, setEnquiryOptions] = useState([]);
  const [manualEnquiry, setManualEnquiry] = useState(false);
  const [accusedOptions, setAccusedOptions] = useState([]);
  const [selectedAccused, setSelectedAccused] = useState("");
  const [searchText, setSearchText] = useState("");
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState("");
  const [formData, setFormData] = useState({
    enquiryCaseNum: "",
    firRegDate: "",
    accusedName: "",
    accusedCnic: "",
    roleOfAccused: "",
    accusedStatus: "",
  });

  const entriesPerPage = 10;

  const fetchRows = async (pageNum) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", pageNum);
      params.set("limit", entriesPerPage);
      if (searchText.trim()) params.set("search", searchText.trim());
      if (statusFilter) params.set("status", statusFilter);
      const res = await fetch(`${API_BASE}/api/case-fir?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch case data");
      const json = await res.json();
      setRows(json.data || []);
      setTotalPages(Math.max(1, Math.ceil(Number(json.totalEntries || 0) / entriesPerPage)));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiryOptions = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/enquiries/list`);
      if (!res.ok) return;
      const json = await res.json();
      setEnquiryOptions(json.data || []);
    } catch (error) {
      console.error("Failed to load enquiry list", error);
    }
  };

  useEffect(() => {
    fetchRows(page);
  }, [page, searchText, statusFilter]);

  useEffect(() => {
    const incomingSearch = location.state?.search;
    if (incomingSearch) {
      setSearchText(incomingSearch);
    }
  }, [location.state]);

  useEffect(() => {
    fetchEnquiryOptions();
  }, []);

  const fetchAccusedOptions = async (enquiryCaseNum) => {
    if (!enquiryCaseNum) {
      setAccusedOptions([]);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/summons/accused?case=${encodeURIComponent(enquiryCaseNum)}`
      );
      if (!res.ok) return;
      const json = await res.json();
      setAccusedOptions(json.data || []);
    } catch (error) {
      console.error("Failed to load accused list", error);
    }
  };

  const handleOpenDialog = (type, row = null) => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    setDialogType(type);
    if (type === "Edit" && row) {
      setFormData({
        enquiryCaseNum: row.enquiry_case_num || "",
        firRegDate: row.fir_reg_date ? row.fir_reg_date.slice(0, 10) : "",
        accusedName: row.accused_name || "",
        accusedCnic: row.accused_cnic || "",
        roleOfAccused: row.role_of_accused || "",
        accusedStatus: row.accused_status || "",
      });
      setSelectedRow(row);
      setSelectedAccused("");
      fetchAccusedOptions(row.enquiry_case_num || "");
    } else {
      setFormData({
        enquiryCaseNum: "",
        firRegDate: "",
        accusedName: "",
        accusedCnic: "",
        roleOfAccused: "",
        accusedStatus: "",
      });
      setSelectedRow(null);
      setSelectedAccused("");
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    if (!formData.enquiryCaseNum.trim()) {
      alert("Enquiry Case Number is required.");
      return;
    }
    const payload = {
      enquiryCaseNum: formData.enquiryCaseNum.trim(),
      firRegDate: formData.firRegDate || null,
      accusedName: formData.accusedName || null,
      accusedCnic: formData.accusedCnic || null,
      roleOfAccused: formData.roleOfAccused || null,
      accusedStatus: formData.accusedStatus || null,
    };
    const url =
      dialogType === "Add"
        ? `${API_BASE}/api/case-fir`
        : `${API_BASE}/api/case-fir/${selectedRow.id}`;
    const method = dialogType === "Add" ? "POST" : "PUT";
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert("Failed to save case.");
      return;
    }
    setOpenDialog(false);
    fetchRows(page);
  };

  const handleAccusedSelect = (value) => {
    setSelectedAccused(value);
    const found = accusedOptions.find((opt) => opt.name === value);
    if (found) {
      setFormData((prev) => ({
        ...prev,
        accusedName: found.name || "",
        accusedCnic: found.cnic || "",
        roleOfAccused: found.role || "",
      }));
    }
  };

  const getStatusChipColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "arrested":
        return "error";
      case "on bail":
        return "warning";
      case "bba":
        return "warning";
      case "interim-173":
        return "info";
      case "complete challan":
        return "success";
      case "po challaned-512":
        return "success";
      case "aquital":
      case "acquittal":
        return "success";
      case "not arrested":
        return "default";
      default:
        return "default";
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      alert("Permission denied");
      return;
    }
    if (!window.confirm("Delete this case record?")) return;
    await fetch(`${API_BASE}/api/case-fir/${row.id}`, { method: "DELETE" });
    fetchRows(page);
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
        <Typography variant="h5">Case/FIR Details</Typography>
        <Button variant="contained" onClick={() => handleOpenDialog("Add")} disabled={!canEdit}>
          Add Case/FIR
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
        <TextField
          label="Search"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          fullWidth
        />
        <FormControl sx={{ minWidth: { xs: "100%", md: 200 } }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {accusedStatusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isMobile ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {rows.map((row) => (
            <Card key={row.id} variant="outlined">
              <CardHeader
                title={row.enquiry_case_num || "Enquiry"}
                subheader={row.complainant_name || "—"}
              />
              <Divider />
              <CardContent>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      FIR Date
                    </Typography>
                    <Typography variant="body2">{formatDisplayDate(row.fir_reg_date) || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2">{row.category_type || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Accused
                    </Typography>
                    <Typography variant="body2">
                      {row.accused_name || "—"} {row.accused_cnic ? `(${row.accused_cnic})` : ""}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2">{row.accused_status || "—"}</Typography>
                  </Box>
                  <Divider />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => handleOpenDialog("Edit", row)} disabled={!canEdit}>
                      Edit
                    </Button>
                    <Button size="small" color="secondary" onClick={() => handleDelete(row)} disabled={!canDelete}>
                      Delete
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {!rows.length && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">No data available.</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sr No</TableCell>
                <TableCell>Enquiry No</TableCell>
                <TableCell>Complainant Name</TableCell>
                <TableCell>FIR Reg Date</TableCell>
                <TableCell>Receiving Date</TableCell>
                <TableCell>Transferred From</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Gist of Allegation</TableCell>
                <TableCell>Accused Name</TableCell>
                <TableCell>Accused CNIC</TableCell>
                <TableCell>Role of Accused</TableCell>
                <TableCell>Status of Accused</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.id} hover>
                  <TableCell>{(page - 1) * entriesPerPage + idx + 1}</TableCell>
                  <TableCell>{row.enquiry_case_num || "—"}</TableCell>
                  <TableCell>{row.complainant_name || "—"}</TableCell>
                  <TableCell>{formatDisplayDate(row.fir_reg_date) || "—"}</TableCell>
                  <TableCell>{formatDisplayDate(row.receiving_date) || "—"}</TableCell>
                  <TableCell>{row.transferred_from || "—"}</TableCell>
                  <TableCell>{row.amount || "—"}</TableCell>
                  <TableCell>{row.category_type || "—"}</TableCell>
                  <TableCell>{row.gist_of_allegation || "—"}</TableCell>
                  <TableCell>{row.accused_name || "—"}</TableCell>
                  <TableCell>{row.accused_cnic || "—"}</TableCell>
                  <TableCell>{row.role_of_accused || "—"}</TableCell>
                <TableCell>
                  {row.accused_status ? (
                    <Chip
                      size="small"
                      label={row.accused_status}
                      color={getStatusChipColor(row.accused_status)}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => handleOpenDialog("Edit", row)} disabled={!canEdit}>
                        Edit
                      </Button>
                      <Button size="small" color="secondary" onClick={() => handleDelete(row)} disabled={!canDelete}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={14}>No data available.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, value) => setPage(value)}
        sx={{ mt: 2, display: "flex", justifyContent: "center" }}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogType} Case/FIR</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={manualEnquiry}
                  onChange={(e) => setManualEnquiry(e.target.checked)}
                />
              }
              label="Manual Enquiry Entry"
            />
            {manualEnquiry ? (
              <TextField
                label="Enquiry Case Number"
                value={formData.enquiryCaseNum}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, enquiryCaseNum: value });
                  fetchAccusedOptions(value);
                }}
                fullWidth
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Enquiry Case Number</InputLabel>
                <Select
                  label="Enquiry Case Number"
                  value={formData.enquiryCaseNum}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, enquiryCaseNum: value });
                    fetchAccusedOptions(value);
                  }}
                >
                  {enquiryOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Accused (from Summon)</InputLabel>
              <Select
                label="Accused (from Summon)"
                value={selectedAccused}
                onChange={(e) => handleAccusedSelect(e.target.value)}
              >
                <MenuItem value="">Manual entry</MenuItem>
                {accusedOptions.map((opt) => (
                  <MenuItem key={`${opt.name}-${opt.cnic}`} value={opt.name}>
                    {opt.name} {opt.cnic ? `(${opt.cnic})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="FIR Registration Date"
              type="date"
              value={formData.firRegDate}
              onChange={(e) => setFormData({ ...formData, firRegDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Accused Name"
              value={formData.accusedName}
              onChange={(e) => setFormData({ ...formData, accusedName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Accused CNIC"
              value={formData.accusedCnic}
              onChange={(e) => setFormData({ ...formData, accusedCnic: e.target.value })}
              fullWidth
            />
            <TextField
              label="Role of Accused"
              value={formData.roleOfAccused}
              onChange={(e) => setFormData({ ...formData, roleOfAccused: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status of Accused</InputLabel>
              <Select
                label="Status of Accused"
                value={formData.accusedStatus}
                onChange={(e) => setFormData({ ...formData, accusedStatus: e.target.value })}
              >
                {accusedStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CaseFirDetails;
